import { useState, useEffect, useCallback } from 'react'
import { supabase, safeQuery } from '../lib/supabase'
import { getCached, setCached } from '../lib/queryCache'
import { useAutoRefresh } from './useAutoRefresh'
import { localFallback, localUpsert, localDelete, raceTimeout } from '../lib/syncEngine'
import { queuedWrite } from '../lib/queuedWrite'

export function useCompanies(filters = {}) {
  const cacheKey = `companies:${JSON.stringify(filters)}`
  const cached = getCached(cacheKey)
  const [companies, setCompanies] = useState(cached || [])
  const [loading, setLoading] = useState(!cached)
  const [error, setError] = useState(null)
  const [total, setTotal] = useState(0)

  const fetchCompanies = useCallback(async () => {
    console.log('Fetching companies...')
    if (!getCached(cacheKey)) setLoading(true)
    setError(null)
    try {
      let query = supabase
        .from('companies')
        .select('*', { count: 'estimated' })
        .order('name', { ascending: true })

      if (filters.entity_id) query = query.eq('entity_id', filters.entity_id)
      if (filters.deal_active !== undefined) query = query.eq('deal_active', filters.deal_active)
      // search_names = name + aliases, so a company found under a former name
      // (pre-acquisition, pre-rebrand) still matches.
      if (filters.search) query = query.or(`name.ilike.%${filters.search}%,search_names.ilike.%${filters.search}%`)
      // Note: company_type filter applied client-side below to avoid 400 if column doesn't exist

      const { data, error: err, count } = await safeQuery(query)
      if (err) {
        setError(err.message)
        if (!getCached(cacheKey)) {
          const local = await localFallback('companies', { orderBy: 'name ASC NULLS LAST' })
          if (local.length) { setCompanies(local); setTotal(local.length) }
        }
        return
      }

      let results = data || []
      // Client-side company_type filter (contains on JSONB array)
      if (filters.company_type) {
        results = results.filter(c =>
          Array.isArray(c.company_type) && c.company_type.includes(filters.company_type)
        )
      }

      setCompanies(results)
      setTotal(filters.company_type ? results.length : (count || 0))
      setCached(cacheKey, results)
    } catch (err) {
      setError(err.message)
      if (!getCached(cacheKey)) {
        const local = await localFallback('companies', { orderBy: 'name ASC NULLS LAST' })
        if (local.length) { setCompanies(local); setTotal(local.length) }
      }
    } finally {
      setLoading(false)
    }
  }, [JSON.stringify(filters), cacheKey])

  useEffect(() => { fetchCompanies() }, [fetchCompanies])

  const silentRefetch = useCallback(() => fetchCompanies(), [fetchCompanies])
  useAutoRefresh(silentRefetch)

  return { companies, loading, error, total, refetch: fetchCompanies }
}

export function useCompany(id) {
  const [company, setCompany] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const fetchCompany = useCallback(async () => {
    if (!id) return
    console.log('Fetching company...')
    setLoading(true)
    setError(null)
    try {
      const { data, error: err } = await safeQuery(
        supabase
          .from('companies')
          .select('*')
          .eq('id', id)
          .single()
      )
      if (err) { setError(err.message); return }
      setCompany(data)
    } catch (err) {
      setError(err.message)
      setCompany(null)
    } finally {
      setLoading(false)
    }
  }, [id])

  useEffect(() => { fetchCompany() }, [fetchCompany])

  return { company, loading, error, refetch: fetchCompany }
}

export async function createCompany(data) {
  const payload = { entity_id: 'chatrone_us', ...data }
  const result = await queuedWrite('companies', 'insert', payload)
  localUpsert('companies', result).catch(() => {})
  return result
}

export async function updateCompany(id, data) {
  const result = await queuedWrite('companies', 'update', data, id)
  localUpsert('companies', result).catch(() => {})
  return result
}

export async function deleteCompany(id) {
  await queuedWrite('companies', 'delete', null, id)
  localDelete('companies', id).catch(() => {})
}

export async function searchCompanies(query) {
  try {
    const { data, error } = await raceTimeout(supabase
      .from('companies')
      .select('id, name, company_type')
      .or(`name.ilike.%${query}%,search_names.ilike.%${query}%`)
      .limit(10), 7000, 'searchCompanies')
    if (error) throw error
    return data || []
  } catch (e) {
    // Fail fast to empty so the picker stays usable; recovery re-runs it.
    console.warn('[searchCompanies]', e?.message)
    return []
  }
}
